/**
 * Renders Trustpilot star ratings on product cards (e.g. Best Sellers grid).
 *
 * Unlike product-reviews.js (single PDP with the full Trustpilot review
 * widget), this handles many products on one page, each with its own SKU,
 * fetched and rendered independently. No MutationObserver / trustbox wiring
 * needed here — cards only need the star rating + review count.
 *
 * Liquid: each card's rating container needs data-sku (and optionally
 * data-url to link the stars back to that product's reviews section):
 *
 * <div class="product-rating flex items-center gap-1"
 *      data-sku="{{ product.selected_or_first_available_variant.sku }}"
 *      data-url="{{ product.url }}"></div>
 */

const TRUSTPILOT_TEMPLATE_ID = '5763bccae0a06d08e809ecbb';
const TRUSTPILOT_BUSINESS_UNIT_ID = '60f97fd710f8e80001cc6857';

function buildTrustpilotEndpoint(sku) {
    const skuParam = `TRUSTPILOT_SKU_VALUE_7674942947497,TRUSTPILOT_SKU_VALUE_42816858554537,${sku}`;
    return `https://widget.trustpilot.com/trustbox-data/${TRUSTPILOT_TEMPLATE_ID}?businessUnitId=${TRUSTPILOT_BUSINESS_UNIT_ID}&locale=en-US&sku=${encodeURIComponent(skuParam)}&reviewsPerPage=10`;
}

async function fetchReviewSummary(sku) {
    try {
        const response = await fetch(buildTrustpilotEndpoint(sku));
        if (!response.ok) throw new Error('Network response was not ok');
        return await response.json();
    } catch (error) {
        console.error(`Error fetching Trustpilot data for SKU ${sku}:`, error.message);
        return false;
    }
}

function renderStars(container, reviewData, sku) {
    if (!reviewData || !reviewData.productReviewsSummary) {
        container.innerHTML = '<p class="text-[13px] text-primaryDarkGray">No reviews yet</p>';
        return;
    }

    const totalRating = reviewData.productReviewsSummary.starsAverage;
    const totalReviews = reviewData.productReviewsSummary.numberOfReviews.total;
    const starCount = Math.round(totalRating);
    const url = container.dataset.url;

    const wrapper = document.createElement(url ? 'a' : 'div');
    if (url) wrapper.href = `${url}#product-reviews-dropdown`;
    wrapper.classList.add('product-review-stars-wrapper', 'flex', 'items-center', 'gap-1');

    for (let i = 1; i <= starCount; i++) {
        const titleId = `starRating-${sku}-${i}`;
        const reviewStar = document.createElement('div');
        reviewStar.classList.add('product-review-star');
        reviewStar.innerHTML = `
            <svg role="img" aria-labelledby="${titleId}" width="18" height="18" viewBox="0 0 46.375586 46.330002" xmlns="http://www.w3.org/2000/svg">
                <title id="${titleId}" lang="en">${totalRating} out of five star rating on Trustpilot</title>
                <g>
                    <path class="tp-star__canvas" fill="#6FC2B6" d="M0 46.330002h46.375586V0H0z"></path>
                    <path class="tp-star__shape" d="M39.533936 19.711433L13.230239 38.80065l3.838216-11.797827L7.02115 19.711433h12.418975l3.837417-11.798624 3.837418 11.798624h12.418975zM23.2785 31.510075l7.183595-1.509576 2.862114 8.800152L23.2785 31.510075z" fill="#FFF"></path>
                </g>
            </svg>`;
        wrapper.appendChild(reviewStar);
    }

    const countEl = document.createElement('div');
    countEl.classList.add('product-review-count');
    countEl.textContent = `(${totalReviews})`;
    wrapper.appendChild(countEl);

    container.innerHTML = '';
    container.appendChild(wrapper);
}

async function renderProductCardRatings() {
    const ratingContainers = document.querySelectorAll('.product-rating[data-sku]');
    if (ratingContainers.length === 0) return;

    await Promise.all(
        Array.from(ratingContainers).map(async (container) => {
            const sku = container.dataset.sku;
            if (!sku) return;
            const reviewData = await fetchReviewSummary(sku);
            renderStars(container, reviewData, sku);
        })
    );
}

document.addEventListener('DOMContentLoaded', renderProductCardRatings);